import React from "react";
import { Link } from "react-router-dom";
import { RxCross1 } from "react-icons/rx";
import { FiDownload } from "react-icons/fi";
import { toast } from "react-toastify";
import Loader from "../Layout/Loader";
import { exportToExcel, exportToPDF } from "../../utils/exportUtils";

const formatDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "—";
  return dt.toLocaleString("en-GB");
};

const formatMoney = (v) => `£${Number(v || 0).toFixed(2)}`;

function DetailTable({ columns, rows, emptyMessage, onRowClick }) {
  if (!rows?.length) {
    return <p className="text-sm text-slate-500 py-6 text-center">{emptyMessage}</p>;
  }
  return (
    <div className="overflow-x-auto max-h-[55vh] border border-slate-200 rounded-lg">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50 sticky top-0 z-10">
          <tr>
            {columns.map((col) => (
              <th key={col.key} className="px-3 py-2 text-left font-semibold text-slate-700 whitespace-nowrap">
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.id}
              className={`border-t border-slate-100 ${onRowClick ? "cursor-pointer hover:bg-slate-50" : ""}`}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              onKeyDown={
                onRowClick
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onRowClick(row);
                      }
                    }
                  : undefined
              }
              tabIndex={onRowClick ? 0 : undefined}
              role={onRowClick ? "button" : undefined}
            >
              {columns.map((col) => (
                <td key={col.key} className="px-3 py-2 text-slate-700">
                  {col.render ? col.render(row) : row[col.key] ?? "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MetricFooter({ footer, metricId }) {
  if (!footer || typeof footer !== "object") return null;
  const items = [];

  if (metricId === "avgOrderValue" && footer.formula) {
    return (
      <div className="mt-4 rounded-lg border border-purple-200 bg-purple-50 px-4 py-3 text-sm text-purple-900">
        <p className="font-semibold">Calculation</p>
        <p className="mt-1 font-mono text-base">{footer.formula}</p>
        {footer.note ? <p className="text-xs text-purple-800 mt-2">{footer.note}</p> : null}
      </div>
    );
  }

  if (footer.note) {
    items.push({ label: "Note", value: footer.note });
  }
  if (footer.totalSales != null) {
    items.push({ label: "Total sales", value: formatMoney(footer.totalSales) });
  }
  if (footer.totalOrders != null) {
    items.push({ label: "Valid paid orders", value: String(footer.totalOrders) });
  }
  if (footer.averageOrderValue != null) {
    items.push({ label: "Average order value", value: formatMoney(footer.averageOrderValue) });
  }
  if (footer.refundedOrders != null) {
    items.push({ label: "Refunded orders", value: String(footer.refundedOrders) });
  }
  if (footer.refundedAmount != null) {
    items.push({ label: "Refunded amount", value: formatMoney(footer.refundedAmount) });
  }
  if (footer.totalItemsSold != null) {
    items.push({ label: "Products sold", value: String(footer.totalItemsSold) });
  }
  if (footer.averageItemsPerOrder != null) {
    items.push({
      label: "Avg items/order",
      value: Number(footer.averageItemsPerOrder).toFixed(2),
    });
  }
  if (footer.totalReturns != null) {
    items.push({ label: "Return requests", value: String(footer.totalReturns) });
  }
  if (footer.totalReturnedItems != null) {
    items.push({ label: "Returned items", value: String(footer.totalReturnedItems) });
  }
  if (footer.buyerProtectionFees != null) {
    items.push({ label: "Buyer protection total", value: formatMoney(footer.buyerProtectionFees) });
  }
  if (footer.sellerPlatformFees != null) {
    items.push({
      label: `Seller platform fees (${footer.platformFeePercent ?? 5}%)`,
      value: formatMoney(footer.sellerPlatformFees),
    });
  }
  if (footer.featuredListingFees != null) {
    items.push({ label: "Featured listing total", value: formatMoney(footer.featuredListingFees) });
  }
  if (footer.eventBannerFees != null) {
    items.push({ label: "Event banner total", value: formatMoney(footer.eventBannerFees) });
  }
  if (footer.serviceBannerFees != null) {
    items.push({ label: "Service banner total", value: formatMoney(footer.serviceBannerFees) });
  }
  if (footer.vaNetEarnings != null) {
    items.push({ label: "VA net (est.)", value: formatMoney(footer.vaNetEarnings) });
  }
  if (footer.vaPendingInEscrow != null) {
    items.push({ label: "VA pending in escrow", value: formatMoney(footer.vaPendingInEscrow) });
  }
  if (footer.escrowOrderCount != null) {
    items.push({ label: "Open escrow orders", value: String(footer.escrowOrderCount) });
  }

  if (!items.length) return null;

  return (
    <dl className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((item) => (
        <div key={item.label} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          <dt className="text-xs text-slate-500">{item.label}</dt>
          <dd className="text-sm font-semibold text-slate-900 mt-1">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

const ORDER_TX_COLUMNS = [
  { key: "date", label: "Date", render: (r) => formatDate(r.date) },
  { key: "reference", label: "Order", render: (r) => r.reference },
  { key: "cartSummary", label: "Items", render: (r) => r.cartSummary || "—" },
  { key: "buyerName", label: "Buyer" },
  { key: "sellerName", label: "Seller" },
  {
    key: "grossAmount",
    label: "Order total",
    render: (r) => formatMoney(r.grossAmount),
  },
  { key: "paymentMethod", label: "Payment" },
  { key: "status", label: "Status" },
];

const BUYER_PROTECTION_COLUMNS = [
  { key: "date", label: "Date", render: (r) => formatDate(r.date) },
  { key: "reference", label: "Order", render: (r) => r.reference },
  { key: "cartSummary", label: "Items", render: (r) => r.cartSummary || "—" },
  { key: "buyerName", label: "Buyer" },
  { key: "sellerName", label: "Seller" },
  {
    key: "buyerProtectionFee",
    label: "Buyer protection fee",
    render: (r) => formatMoney(r.buyerProtectionFee),
  },
  {
    key: "grossAmount",
    label: "Order total",
    render: (r) => formatMoney(r.grossAmount),
  },
  { key: "paymentMethod", label: "Payment" },
  { key: "status", label: "Status" },
];

const SELLER_PLATFORM_COLUMNS = [
  { key: "date", label: "Date", render: (r) => formatDate(r.date) },
  { key: "reference", label: "Order", render: (r) => r.reference },
  { key: "cartSummary", label: "Items", render: (r) => r.cartSummary || "—" },
  { key: "buyerName", label: "Buyer" },
  { key: "sellerName", label: "Seller" },
  {
    key: "platformFeeAmount",
    label: "Seller platform fee",
    render: (r) => formatMoney(r.platformFeeAmount),
  },
  {
    key: "grossAmount",
    label: "Order total",
    render: (r) => formatMoney(r.grossAmount),
  },
  { key: "paymentMethod", label: "Payment" },
  { key: "status", label: "Status" },
];

const VA_NET_COLUMNS = [
  { key: "date", label: "Date", render: (r) => formatDate(r.date) },
  { key: "typeLabel", label: "Type" },
  { key: "reference", label: "Reference", render: (r) => r.reference },
  { key: "cartSummary", label: "Items", render: (r) => r.cartSummary || "—" },
  { key: "buyerName", label: "Buyer" },
  { key: "sellerName", label: "Seller" },
  {
    key: "buyerProtectionFee",
    label: "Buyer protection",
    render: (r) =>
      Number(r.buyerProtectionFee || 0) > 0 ? formatMoney(r.buyerProtectionFee) : "—",
  },
  {
    key: "platformFeeAmount",
    label: "Seller platform fee",
    render: (r) =>
      Number(r.platformFeeAmount || 0) > 0 ? formatMoney(r.platformFeeAmount) : "—",
  },
  {
    key: "stripeFeeAmount",
    label: "Stripe fee (est.)",
    render: (r) =>
      Number(r.stripeFeeAmount || 0) > 0 ? formatMoney(r.stripeFeeAmount) : "—",
  },
  {
    key: "vaEarnings",
    label: "VA net (est.)",
    render: (r) => formatMoney(r.vaEarnings),
  },
  {
    key: "grossAmount",
    label: "Order total",
    render: (r) =>
      r.type === "marketplace_order" ? formatMoney(r.grossAmount) : "—",
  },
  { key: "paymentMethod", label: "Payment" },
  { key: "status", label: "Status" },
];

const METRICS_WITH_EXPORT = new Set([
  "totalSales",
  "totalOrders",
  "avgOrderValue",
  "refunded",
  "buyerProtection",
  "sellerPlatform",
  "vaNet",
  "vaPendingEscrow",
  "productsSold",
  "returns",
  "featuredListing",
  "eventBanner",
  "serviceBanner",
]);

const VA_ESCROW_COLUMNS = [
  { key: "date", label: "Date", render: (r) => formatDate(r.date) },
  { key: "reference", label: "Order", render: (r) => r.reference },
  { key: "sellerName", label: "Seller" },
  { key: "buyerName", label: "Buyer" },
  {
    key: "vaEarnings",
    label: "VA net (est.)",
    render: (r) => formatMoney(r.vaEarnings),
  },
  {
    key: "grossAmount",
    label: "Order total",
    render: (r) => formatMoney(r.grossAmount),
  },
  { key: "paymentMethod", label: "Payment" },
  { key: "status", label: "Status" },
];

const formatExportDate = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  return dt.toLocaleString("en-GB");
};

function getMetricDisplayColumns(metricId) {
  switch (metricId) {
    case "vaNet":
      return VA_NET_COLUMNS;
    case "vaPendingEscrow":
      return VA_ESCROW_COLUMNS;
    case "buyerProtection":
      return BUYER_PROTECTION_COLUMNS;
    case "sellerPlatform":
      return SELLER_PLATFORM_COLUMNS;
    case "productsSold":
      return PRODUCTS_SOLD_COLUMNS;
    case "returns":
      return RETURNS_COLUMNS;
    case "featuredListing":
    case "eventBanner":
    case "serviceBanner":
      return PROMO_TX_COLUMNS;
    default:
      return ORDER_TX_COLUMNS;
  }
}

function getMetricExportColumns(metricId) {
  switch (metricId) {
    case "vaPendingEscrow":
      return [
        { field: "date", headerName: "Date", getValue: (r) => formatExportDate(r.date) },
        { field: "reference", headerName: "Order" },
        { field: "sellerName", headerName: "Seller" },
        { field: "buyerName", headerName: "Buyer" },
        {
          field: "vaEarnings",
          headerName: "VA net (est.)",
          getValue: (r) => Number(r.vaEarnings || 0).toFixed(2),
        },
        {
          field: "grossAmount",
          headerName: "Order total",
          getValue: (r) => Number(r.grossAmount || 0).toFixed(2),
        },
        { field: "paymentMethod", headerName: "Payment" },
        { field: "status", headerName: "Status" },
      ];
    case "vaNet":
      return [
        { field: "date", headerName: "Date", getValue: (r) => formatExportDate(r.date) },
        { field: "typeLabel", headerName: "Type" },
        { field: "reference", headerName: "Reference" },
        { field: "cartSummary", headerName: "Items" },
        { field: "buyerName", headerName: "Buyer" },
        { field: "sellerName", headerName: "Seller" },
        {
          field: "buyerProtectionFee",
          headerName: "Buyer protection",
          getValue: (r) => Number(r.buyerProtectionFee || 0).toFixed(2),
        },
        {
          field: "platformFeeAmount",
          headerName: "Seller platform fee",
          getValue: (r) => Number(r.platformFeeAmount || 0).toFixed(2),
        },
        {
          field: "stripeFeeAmount",
          headerName: "Stripe fee (est.)",
          getValue: (r) => Number(r.stripeFeeAmount || 0).toFixed(2),
        },
        {
          field: "vaEarnings",
          headerName: "VA net earnings (est.)",
          getValue: (r) => Number(r.vaEarnings || 0).toFixed(2),
        },
        {
          field: "grossAmount",
          headerName: "Order total",
          getValue: (r) =>
            r.type === "marketplace_order" ? Number(r.grossAmount || 0).toFixed(2) : "",
        },
        { field: "paymentMethod", headerName: "Payment" },
        { field: "status", headerName: "Status" },
      ];
    case "buyerProtection":
      return [
        { field: "date", headerName: "Date", getValue: (r) => formatExportDate(r.date) },
        { field: "reference", headerName: "Order" },
        { field: "cartSummary", headerName: "Items" },
        { field: "buyerName", headerName: "Buyer" },
        { field: "sellerName", headerName: "Seller" },
        {
          field: "buyerProtectionFee",
          headerName: "Buyer protection fee",
          getValue: (r) => Number(r.buyerProtectionFee || 0).toFixed(2),
        },
        {
          field: "grossAmount",
          headerName: "Order total",
          getValue: (r) => Number(r.grossAmount || 0).toFixed(2),
        },
        { field: "paymentMethod", headerName: "Payment" },
        { field: "status", headerName: "Status" },
      ];
    case "sellerPlatform":
      return [
        { field: "date", headerName: "Date", getValue: (r) => formatExportDate(r.date) },
        { field: "reference", headerName: "Order" },
        { field: "cartSummary", headerName: "Items" },
        { field: "buyerName", headerName: "Buyer" },
        { field: "sellerName", headerName: "Seller" },
        {
          field: "platformFeeAmount",
          headerName: "Seller platform fee",
          getValue: (r) => Number(r.platformFeeAmount || 0).toFixed(2),
        },
        {
          field: "grossAmount",
          headerName: "Order total",
          getValue: (r) => Number(r.grossAmount || 0).toFixed(2),
        },
        { field: "paymentMethod", headerName: "Payment" },
        { field: "status", headerName: "Status" },
      ];
    case "productsSold":
      return [
        { field: "date", headerName: "Date", getValue: (r) => formatExportDate(r.date) },
        { field: "reference", headerName: "Order" },
        { field: "productName", headerName: "Product" },
        { field: "qty", headerName: "Qty" },
        {
          field: "lineTotal",
          headerName: "Line total",
          getValue: (r) => Number(r.lineTotal || 0).toFixed(2),
        },
        { field: "buyerName", headerName: "Buyer" },
        { field: "sellerName", headerName: "Seller" },
        { field: "orderStatus", headerName: "Order status" },
      ];
    case "returns":
      return [
        { field: "date", headerName: "Date" },
        { field: "orderId", headerName: "Order" },
        { field: "type", headerName: "Type" },
        { field: "productName", headerName: "Product" },
        { field: "quantity", headerName: "Qty" },
        { field: "amount", headerName: "Amount" },
        { field: "status", headerName: "Status" },
      ];
    case "featuredListing":
    case "eventBanner":
    case "serviceBanner":
      return [
        { field: "date", headerName: "Date", getValue: (r) => formatExportDate(r.date) },
        { field: "typeLabel", headerName: "Type" },
        { field: "reference", headerName: "Reference" },
        {
          field: "grossAmount",
          headerName: "Amount",
          getValue: (r) => Number(r.grossAmount || 0).toFixed(2),
        },
        { field: "paymentMethod", headerName: "Payment" },
        { field: "status", headerName: "Status" },
      ];
    default:
      return [
        { field: "date", headerName: "Date", getValue: (r) => formatExportDate(r.date) },
        { field: "reference", headerName: "Order" },
        { field: "cartSummary", headerName: "Items" },
        { field: "buyerName", headerName: "Buyer" },
        { field: "sellerName", headerName: "Seller" },
        {
          field: "grossAmount",
          headerName: "Order total",
          getValue: (r) => Number(r.grossAmount || 0).toFixed(2),
        },
        { field: "paymentMethod", headerName: "Payment" },
        { field: "status", headerName: "Status" },
      ];
  }
}

function buildMetricExportRows(metricId, rows) {
  const columns = getMetricExportColumns(metricId);
  return rows.map((row, idx) => {
    const out = { id: row.id || String(idx) };
    columns.forEach((col) => {
      out[col.field] = col.getValue ? col.getValue(row) : row[col.field] ?? "";
    });
    return out;
  });
}

function exportMetricModal(metricId, rows, periodLabel, format) {
  if (!rows?.length) {
    toast.error("No data to export.");
    return;
  }
  const title = METRIC_MODAL_TITLES[metricId] || "Sales metric";
  const columns = getMetricExportColumns(metricId);
  const exportRows = buildMetricExportRows(metricId, rows);
  const filename = `${title.replace(/\s+/g, "_")}_${periodLabel || "all"}_${new Date().toISOString().split("T")[0]}`;
  const pageInfo = {
    title: `${title} — ${periodLabel || "All time"}`,
    description: METRIC_DESCRIPTIONS[metricId] || "",
    exportDate: new Date().toLocaleString("en-GB"),
  };
  try {
    if (format === "pdf") {
      exportToPDF(exportRows, columns, filename, pageInfo);
    } else {
      exportToExcel(exportRows, columns, filename, pageInfo);
    }
    toast.success(`${format === "pdf" ? "PDF" : "Excel"} exported successfully`);
  } catch (error) {
    toast.error(`Export failed: ${error.message || "Unknown error"}`);
  }
}

const PRODUCTS_SOLD_COLUMNS = [
  { key: "date", label: "Date", render: (r) => formatDate(r.date) },
  {
    key: "reference",
    label: "Order",
    render: (r) =>
      r.orderId ? (
        <Link
          to={`/admin-order/${r.orderId}`}
          className="text-[#38513B] font-medium hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {r.reference}
        </Link>
      ) : (
        r.reference
      ),
  },
  { key: "productName", label: "Product" },
  { key: "qty", label: "Qty" },
  {
    key: "lineTotal",
    label: "Line total",
    render: (r) => formatMoney(r.lineTotal),
  },
  { key: "buyerName", label: "Buyer" },
  { key: "sellerName", label: "Seller" },
  { key: "orderStatus", label: "Order status" },
];

const RETURNS_COLUMNS = [
  { key: "date", label: "Date", render: (r) => r.date },
  {
    key: "orderId",
    label: "Order",
    render: (r) => `#${r.orderId}`,
  },
  { key: "type", label: "Type" },
  { key: "productName", label: "Product" },
  { key: "quantity", label: "Qty" },
  {
    key: "amount",
    label: "Amount",
    render: (r) => (r.amount != null && r.amount !== "" ? `£${r.amount}` : "—"),
  },
  { key: "status", label: "Status" },
];

const PROMO_TX_COLUMNS = [
  { key: "date", label: "Date", render: (r) => formatDate(r.date) },
  { key: "typeLabel", label: "Type" },
  { key: "reference", label: "Reference" },
  {
    key: "grossAmount",
    label: "Amount",
    render: (r) => formatMoney(r.grossAmount),
  },
  { key: "paymentMethod", label: "Payment" },
  { key: "status", label: "Status" },
];

export function SalesMetricCard({ label, value, subValue, tone = "slate", onClick, className = "" }) {
  const tones = {
    blue: "from-blue-50 to-blue-100 border-blue-200 text-blue-700 text-blue-900",
    green: "from-green-50 to-green-100 border-green-200 text-green-700 text-green-900",
    purple: "from-purple-50 to-purple-100 border-purple-200 text-purple-700 text-purple-900",
    red: "from-red-50 to-red-100 border-red-200 text-red-700 text-red-900",
    amber: "from-amber-50 to-amber-100 border-amber-200 text-amber-700 text-amber-900",
    cyan: "from-cyan-50 to-cyan-100 border-cyan-200 text-cyan-700 text-cyan-900",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800 text-emerald-900",
    teal: "border-teal-200 bg-teal-50 text-teal-800 text-teal-900",
    indigo: "border-indigo-200 bg-indigo-50 text-indigo-800 text-indigo-900",
    violet: "border-violet-200 bg-violet-50 text-violet-800 text-violet-900",
    sky: "border-sky-200 bg-sky-50 text-sky-800 text-sky-900",
    slate: "border-slate-300 bg-slate-50 text-slate-700 text-slate-900",
  };
  const parts = String(tones[tone] || tones.slate).split(" ");
  const isGradient =
    tone === "blue" ||
    tone === "green" ||
    tone === "purple" ||
    tone === "red" ||
    tone === "amber" ||
    tone === "cyan";
  const base = isGradient
    ? `bg-gradient-to-br ${parts.slice(0, 3).join(" ")}`
    : parts.slice(0, 3).join(" ");
  const perfClass = "transform-gpu [contain:paint]";
  const labelCls = parts[3] || "text-slate-700";
  const valueCls = parts[4] || "text-slate-900";

  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={`rounded-lg p-4 border text-left w-full transition ${base} ${perfClass} ${
        onClick
          ? "cursor-pointer hover:shadow-md hover:ring-2 hover:ring-[#38513b]/30 focus:outline-none focus:ring-2 focus:ring-[#38513b]"
          : ""
      } ${className}`}
    >
      <p className={`text-sm font-medium mb-1 ${labelCls}`}>{label}</p>
      <p className={`text-2xl font-bold ${valueCls}`}>{value}</p>
      {subValue ? <p className={`text-xs mt-1 ${labelCls}`}>{subValue}</p> : null}
      {onClick ? <p className="text-[10px] mt-2 opacity-70">Tap for details</p> : null}
    </Tag>
  );
}

export function RevenueDetailModal({ open, title, description, onClose, children }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-4">
          <div>
            <h3 className="text-xl font-semibold text-slate-900">{title}</h3>
            {description ? <p className="text-sm text-slate-600 mt-1">{description}</p> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100"
            aria-label="Close"
          >
            <RxCross1 size={22} />
          </button>
        </div>
        <div className="p-6 overflow-y-auto flex-1">{children}</div>
      </div>
    </div>
  );
}

const METRIC_DESCRIPTIONS = {
  totalSales: "Every valid paid marketplace order in the selected period.",
  totalOrders: "Valid paid marketplace orders (excludes cancelled/refunded statuses).",
  avgOrderValue: "Each order total below; summary shows how the average is calculated.",
  refunded: "Orders with refund, return, or cancellation status.",
  productsSold: "Line items from valid paid orders.",
  returns: "Return and refund line items.",
  buyerProtection: "Orders with buyer protection fee charged at checkout.",
  sellerPlatform: "Orders with seller platform fee applied.",
  featuredListing: "Standalone featured payments and checkout featured fees.",
  eventBanner: "Event banner promotion payments.",
  serviceBanner: "Service banner promotion payments.",
  vaNet: "Each row shows estimated VA net earnings (buyer protection + seller platform fee − Stripe fee for orders, or the promotion/featured payment amount). Order total is shown for reference only.",
  vaPendingEscrow:
    "Estimated VA net earnings on open orders still in escrow (awaiting buyer delivery confirmation). This is a live snapshot and is not limited to the sales date filter.",
};

export function renderMetricModalBody(metricId, ctx) {
  const {
    metricRows = [],
    metricFooter = null,
    metricLoading = false,
    metricTruncated = false,
    onTransactionSelect,
    periodLabel = "All time",
  } = ctx;

  if (metricLoading) {
    return (
      <div className="py-12 flex justify-center">
        <Loader />
      </div>
    );
  }

  const countLabel = `${metricRows.length} record${metricRows.length === 1 ? "" : "s"}`;
  const description = METRIC_DESCRIPTIONS[metricId];
  const onTxClick =
    onTransactionSelect &&
    ["totalSales", "totalOrders", "avgOrderValue", "refunded", "buyerProtection", "sellerPlatform", "vaNet", "vaPendingEscrow"].includes(
      metricId
    )
      ? (row) => {
          if (row.type === "marketplace_order" || row.orderId) onTransactionSelect(row);
        }
      : undefined;

  let table = null;

  switch (metricId) {
    case "totalSales":
    case "totalOrders":
    case "avgOrderValue":
    case "refunded":
    case "buyerProtection":
    case "sellerPlatform":
    case "vaNet":
      table = (
        <DetailTable
          columns={getMetricDisplayColumns(metricId)}
          rows={metricRows}
          emptyMessage="No orders in this period."
          onRowClick={onTxClick}
        />
      );
      break;
    case "productsSold":
      table = (
        <DetailTable
          columns={PRODUCTS_SOLD_COLUMNS}
          rows={metricRows}
          emptyMessage="No product line items in this period."
        />
      );
      break;
    case "returns":
      table = (
        <DetailTable
          columns={RETURNS_COLUMNS}
          rows={metricRows}
          emptyMessage="No returns in this period."
        />
      );
      break;
    case "featuredListing":
    case "eventBanner":
    case "serviceBanner":
      table = (
        <DetailTable
          columns={PROMO_TX_COLUMNS}
          rows={metricRows}
          emptyMessage="No payments in this period."
        />
      );
      break;
    case "vaPendingEscrow":
      table = (
        <DetailTable
          columns={VA_ESCROW_COLUMNS}
          rows={metricRows}
          emptyMessage="No open orders in escrow."
          onRowClick={onTxClick}
        />
      );
      break;
    case "vaReleased":
      return (
        <p className="text-sm text-slate-700 leading-relaxed">
          VA fees already released to sellers are aggregated from payout-released orders. See
          individual orders in the transactions table or open an order in admin for full payout
          history.
        </p>
      );
    case "customerEscrow": {
      const { escrowSummary = {} } = ctx;
      return (
        <div className="text-sm text-slate-700 leading-relaxed space-y-3">
          <p>
            Customer checkout totals held until delivery is confirmed:{" "}
            <strong>£{Number(escrowSummary.moneyInEscrow || 0).toFixed(2)}</strong> across{" "}
            {escrowSummary.escrowOrderCount || 0} order(s).
          </p>
          <p className="text-slate-600">
            Stripe/card: £{Number(escrowSummary.moneyInEscrowStripe || 0).toFixed(2)} · Wallet: £
            {Number(escrowSummary.moneyInEscrowWallet || 0).toFixed(2)}
          </p>
          <p>
            Use the <strong>Escrow orders</strong> table on this page for the full order list.
          </p>
        </div>
      );
    }
    default:
      return null;
  }

  const canExport = METRICS_WITH_EXPORT.has(metricId) && metricRows.length > 0;

  return (
    <>
      {description ? <p className="text-sm text-slate-600 mb-3">{description}</p> : null}
      {canExport ? (
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <button
            type="button"
            onClick={() => exportMetricModal(metricId, metricRows, periodLabel, "excel")}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            <FiDownload size={16} />
            Export Excel
          </button>
          <button
            type="button"
            onClick={() => exportMetricModal(metricId, metricRows, periodLabel, "pdf")}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            <FiDownload size={16} />
            Export PDF
          </button>
        </div>
      ) : null}
      <p className="text-xs font-medium text-slate-500 mb-2">{countLabel}</p>
      {onTxClick ? (
        <p className="text-xs text-slate-500 mb-2">Click a row to open transaction details.</p>
      ) : null}
      {metricTruncated ? (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
          Dataset capped at server limit — counts on cards may include more orders than listed here.
        </p>
      ) : null}
      {table}
      <MetricFooter footer={metricFooter} metricId={metricId} />
    </>
  );
}

export const METRIC_MODAL_TITLES = {
  totalSales: "Total sales",
  totalOrders: "Total orders",
  avgOrderValue: "Average order value",
  refunded: "Refunded orders",
  productsSold: "Products sold",
  returns: "Returns",
  buyerProtection: "Buyer protection fees",
  sellerPlatform: "Seller platform fees",
  featuredListing: "Featured listing fees",
  eventBanner: "Event banner fees",
  serviceBanner: "Service banner fees",
  vaNet: "VA net earnings",
  vaPendingEscrow: "VA pending in escrow",
  vaReleased: "VA released to sellers",
  customerEscrow: "Customer funds in escrow",
};
